import React, { useEffect, useState, useRef } from "react";
import {useDispatch, useSelector} from "react-redux";
import {Link, useNavigate} from "react-router-dom";
import {useFormik} from "formik";
import {Alert, CircularProgress} from "@mui/material";
import ReCAPTCHA from "react-google-recaptcha";
import Header from "../components/Header";
import TextBox from "../components/TextBox";
import {hostSignup} from "../store/thunks/hostActions";
import {hostSignupReset} from "../store/slices/hostSlices";
import {registrationSchema} from "../schemas";
import logo from "../icons/BellhoptLogoMedRes.png";

const propertyManagementOptions = [
    {label: "Streamline", value: "streamline"},
    {label: "Guesty", value: "guesty"},
    {label: "Hospitable", value: "hospitable"},
    {label: "Other", value: "other"},
];

const SAVED_SIGNUP_KEY = "saved_signup_fields";

const defaultSignupValues = {
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    password: "",
    isManager: "",
    propertyManagementSoftware: "",
    selfOnboarding: "",
    companyName: "",
    address: {
        line1: "",
        line2: "",
        city: "",
        state: "",
        zipCode: "",
    },
};

const SignupScreen = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [signupError, setSignupError] = useState(null);
    const [captchaValue, setCaptchaValue] = useState(null);
    const recaptchaRef = useRef(null);

    const [savedConfirmPassword] = useState(() => {
        const saved = sessionStorage.getItem(SAVED_SIGNUP_KEY);
        if (saved) {
            try { return JSON.parse(saved).confirmPassword || ""; } catch { /* ignore */ }
        }
        return "";
    });

    const [initialValues] = useState(() => {
        const saved = sessionStorage.getItem(SAVED_SIGNUP_KEY);
        if (saved) {
            sessionStorage.removeItem(SAVED_SIGNUP_KEY);
            try {
                const { confirmPassword: _, ...formValues } = JSON.parse(saved);
                return { ...defaultSignupValues, ...formValues };
            } catch { /* ignore */ }
        }
        return defaultSignupValues;
    });

    const onCaptchaChange = (token) => {
        setCaptchaValue(token);
    };

    const onCaptchaExpired = () => {
        sessionStorage.setItem(SAVED_SIGNUP_KEY, JSON.stringify({ ...values, confirmPassword }));
        window.location.reload();
    };

    const {values, errors, touched, handleBlur, handleChange, getIn} =
        useFormik({
            initialValues: initialValues,
            validationSchema: registrationSchema,
        });

    const {loading, success, error} = useSelector((state) => {
        return state.hostSignUp;
    });
    const {hostInfo} = useSelector((state) => {
        return state.host;
    });

    const [confirmPassword, setConfirmPassword] = useState(savedConfirmPassword);

    const handleSignUp = () => {
        if (!captchaValue) {
            setSignupError("Please verify that you are not a robot.");
            return;
        }

        if (errors.address && Object.keys(errors.address).length > 0) {
            console.log("errors", errors.address);
            setSignupError(errors.address[Object.keys(errors.address)[0]]);
            return;
        }

        if (Object.keys(errors).length > 0) {
            console.log("errors", errors.address);
            setSignupError(errors[Object.keys(errors)[0]]);
            return;
        }

        if (Object.keys(errors).length === 0 && values.password === confirmPassword && captchaValue) {
            dispatch(hostSignup({...values, captchaResponse: captchaValue}));
        }
    };

    useEffect(() => {
        if (hostInfo) {
            navigate("/host");
        }
        if (success) {
            navigate("/please-verify");
            dispatch(hostSignupReset());
        }
        if (error) {
            setTimeout(() => {
                dispatch(hostSignupReset());
            }, 2000);
        }
    }, [success, navigate, hostInfo, error]);

    useEffect(() => {
        setTimeout(() => {
            signupError && setSignupError(null);
        }, 3000);
    }, [signupError]);

    return (
        <div className="min-h-screen bg-white">
            <Header />

            {error && (
                <Alert
                    severity="error"
                    sx={{
                        position: "fixed",
                        width: "90%",
                        margin: "auto",
                        top: 20,
                        left: 0,
                        right: 0,
                        zIndex: 10,
                    }}
                >
                    <strong>{error}</strong>
                </Alert>
            )}
            {signupError && (
                <Alert
                    severity="error"
                    sx={{
                        position: "fixed",
                        width: "90%",
                        margin: "auto",
                        top: 20,
                        left: 0,
                        right: 0,
                        zIndex: 10,
                    }}
                >
                    {signupError}
                </Alert>
            )}

            <div className="flex-1 flex justify-center px-4 sm:px-6 lg:px-8 py-12 overflow-y-auto">
                <div className="max-w-md w-full">
                    {/* Hero Section */}
                    <div className="text-center mb-8">
                        <div className="inline-block mb-6">
                            <img src={logo} alt="Bellhopt Logo" className="h-24 w-auto mx-auto" />
                        </div>
                        <h1 className="text-3xl font-bold text-gray-900 mb-2" style={{ letterSpacing: '-0.5px' }}>Create your account</h1>
                        <p className="text-gray-500 text-base">Join Bellhopt and start managing your properties</p>
                    </div>

                    {/* Sign Up Form Card */}
                    <div className="bg-gray-100 rounded-2xl p-8 border border-gray-200">
                        <div className="text-center mb-6">
                            <h2 className="text-xl font-bold text-gray-900 mb-1">Sign Up</h2>
                            <p className="text-gray-500 text-sm">
                                Already have an account?{" "}
                                <Link to="/login" className="font-semibold text-gray-900 hover:underline transition-colors">
                                    Sign In
                                </Link>
                            </p>
                        </div>

                        <div className="form space-y-4">
                            <div className="flex items-center gap-x-2">
                                <TextBox
                                    label={"First Name"}
                                    name={"firstName"}
                                    value={values.firstName}
                                    onChange={handleChange}
                                    errors={errors}
                                    handleBlur={handleBlur}
                                />
                                <TextBox
                                    label={"Last Name"}
                                    name={"lastName"}
                                    value={values.lastName}
                                    onChange={handleChange}
                                    errors={errors}
                                    handleBlur={handleBlur}
                                />
                            </div>

                            <TextBox label="Email" name="email" value={values.email} onChange={handleChange}
                                     errors={errors} handleBlur={handleBlur}/>
                            <TextBox label="Mobile Number" name="phoneNumber" value={values.phoneNumber}
                                     onChange={handleChange} errors={errors} handleBlur={handleBlur}/>

                            <div className="flex justify-between py-4 items-center gap-4 flex-wrap">
                                <label htmlFor="isManager" className="text-gray-900 font-medium text-sm">Are you a Property Manager?</label>
                                <div className="flex">
                                    <label className="mx-2 text-gray-700 cursor-pointer text-sm">
                                        <input type="radio" name="isManager" value="true" onChange={handleChange}
                                               onBlur={handleBlur}/> Yes
                                    </label>
                                    <label className="mx-2 text-gray-700 cursor-pointer text-sm">
                                        <input type="radio" name="isManager" value="false" onChange={handleChange}
                                               onBlur={handleBlur}/> No
                                    </label>
                                </div>
                            </div>

                            <div className="w-full py-4">
                                <label className="block text-left mb-2 text-gray-900 font-medium text-sm" htmlFor="propertyManagementSoftware">
                                    Which property management software are you using?
                                </label>
                                <select
                                    id="propertyManagementSoftware"
                                    name="propertyManagementSoftware"
                                    value={values.propertyManagementSoftware}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all duration-200"
                                >
                                    <option value="">Select PMS</option>
                                    {propertyManagementOptions.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                                {touched.propertyManagementSoftware && errors.propertyManagementSoftware && (
                                    <p className="text-red-600 text-sm mt-1">{errors.propertyManagementSoftware}</p>
                                )}
                            </div>

                            <div className="w-full mobile:w-full">
                                <TextBox
                                    label={"Company Name"}
                                    name={"companyName"}
                                    type={"text"}
                                    value={values.companyName}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    errors={errors}
                                />
                            </div>

                            <div className="flex items-center gap-x-7 mobile:flex-col">
                                <TextBox
                                    label={"Address Line 1"}
                                    name={"address.line1"}
                                    type={"text"}
                                    value={values.address.line1}
                                    onChange={handleChange}
                                    errors={errors}
                                    getIn={getIn}
                                    handleBlur={handleBlur}
                                />
                                <TextBox
                                    label={"Address Line 2"}
                                    name={"address.line2"}
                                    type={"text"}
                                    value={values.address.line2}
                                    onChange={handleChange}
                                    errors={errors}
                                    getIn={getIn}
                                    handleBlur={handleBlur}
                                />
                            </div>
                            <div className="flex items-center gap-x-7 mobile:flex-col">
                                <TextBox
                                    label={"City"}
                                    name={"address.city"}
                                    type={"text"}
                                    onChange={handleChange}
                                    value={values.address.city}
                                    errors={errors}
                                    getIn={getIn}
                                    handleBlur={handleBlur}
                                />
                                <TextBox
                                    label={"State"}
                                    name={"address.state"}
                                    type={"text"}
                                    value={values.address.state}
                                    onChange={handleChange}
                                    errors={errors}
                                    getIn={getIn}
                                    handleBlur={handleBlur}
                                />
                                <TextBox
                                    label={"Zip Code"}
                                    name={"address.zipCode"}
                                    type={"text"}
                                    value={values.address.zipCode}
                                    onChange={handleChange}
                                    errors={errors}
                                    getIn={getIn}
                                    handleBlur={handleBlur}
                                />
                            </div>
                            <div className="flex flex-col items-center w-full py-4">
                                <p className="mb-3 text-center text-gray-500 font-medium text-sm max-w-xs leading-relaxed">
                                    Self-signing up? We'll schedule an onboarding call to help you get started.
                                </p>
                                <div className="flex justify-center gap-4">
                                    <label htmlFor="selfOnboardYes" className="flex items-center text-gray-700 cursor-pointer text-sm">
                                        <input
                                            className="mr-2"
                                            type="radio"
                                            id="selfOnboardYes"
                                            name="selfOnboarding"
                                            value="true"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            checked={values.selfOnboarding === "true"}
                                            required
                                        />
                                        Yes
                                    </label>
                                    <label htmlFor="selfOnboardNo" className="flex items-center text-gray-700 cursor-pointer text-sm">
                                        <input
                                            className="mr-2"
                                            type="radio"
                                            id="selfOnboardNo"
                                            name="selfOnboarding"
                                            value="false"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            checked={values.selfOnboarding === "false"}
                                            required
                                        />
                                        No
                                    </label>
                                </div>
                            </div>
                            <TextBox
                                label={"Password"}
                                name={"password"}
                                type={"password"}
                                value={values.password}
                                onChange={handleChange}
                                errors={errors}
                                handleBlur={handleBlur}
                            />
                            {errors.password && touched.password ? (
                                <p className="text-red-600 text-sm">{errors.password}</p>
                            ) : null}

                            <TextBox
                                label={"Confirm Password"}
                                name={"confirmPassword"}
                                type={"password"}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                            />

                            {values.password !== confirmPassword && (
                                <p className="text-red-600 text-sm">Password does not match</p>
                            )}

                            <ReCAPTCHA ref={recaptchaRef} sitekey="6LfjWG8pAAAAAPaJUlEtOre0XeDd4K6I43dsBGTq" onChange={onCaptchaChange} onExpired={onCaptchaExpired} className="my-4 flex justify-center [&>div]:scale-90" />

                            <button
                                type="button"
                                onClick={() => handleSignUp()}
                                disabled={loading}
                                className="w-full flex justify-center items-center py-4 px-6 rounded-xl text-base font-bold text-white bg-gray-900 hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 mt-6"
                            >
                                {loading ? (
                                    <>
                                        <CircularProgress color="inherit" className="mr-3" size={24} />
                                        Creating account...
                                    </>
                                ) : (
                                    "Sign Up to Bellhopt"
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="mt-8 text-center">
                        <p className="text-sm text-gray-400">
                            By signing up, you agree to our{" "}
                            <Link to="/privacy" className="text-gray-500 hover:text-gray-900 transition-colors">Privacy Policy</Link>
                            {" "}and{" "}
                            <Link to="/terms" className="text-gray-500 hover:text-gray-900 transition-colors">Terms of Service</Link>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SignupScreen;
